import { type ReactNode } from 'react'

/* ==========================================================================
   PageContainer Component
   Based on design.md specifications
   Centered content with configurable max-width
   ========================================================================== */

export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

export interface PageContainerProps {
  /** Page content */
  children: ReactNode
  /** Container max-width size */
  size?: ContainerSize
  /** Use warm-beige background (default: true) */
  withBackground?: boolean
  /** Add vertical padding (default: true) */
  withPadding?: boolean
  /** Additional CSS classes */
  className?: string
}

const sizeStyles: Record<ContainerSize, string> = {
  sm: 'max-w-[640px]', // Login, simple forms
  md: 'max-w-[768px]', // Provider prescription form
  lg: 'max-w-[1024px]', // Admin dashboards
  xl: 'max-w-[1280px]', // Full admin layouts
  full: 'max-w-full', // Full width
}

/**
 * Centered page container with configurable max-width.
 * Uses warm-beige background and responsive padding per design.md.
 *
 * @example
 * ```tsx
 * // Provider form layout (768px max)
 * <PageContainer size="md">
 *   <PrescriptionForm />
 * </PageContainer>
 *
 * // Login page (640px max)
 * <PageContainer size="sm">
 *   <LoginForm />
 * </PageContainer>
 *
 * // Admin dashboard (1024px max)
 * <PageContainer size="lg">
 *   <Dashboard />
 * </PageContainer>
 * ```
 */
export function PageContainer({
  children,
  size = 'md',
  withBackground = true,
  withPadding = true,
  className = '',
}: PageContainerProps) {
  return (
    <div
      className={`
        min-h-[calc(100vh-64px)]
        ${withBackground ? 'bg-warm-beige' : ''}
        ${withPadding ? 'py-6 sm:py-8 lg:py-12' : ''}
      `}
    >
      <div
        className={`
          mx-auto
          ${sizeStyles[size]}
          ${withPadding ? 'px-4 sm:px-6' : ''}
          ${className}
        `}
      >
        {children}
      </div>
    </div>
  )
}

export default PageContainer
